/**
 * Standalone entry point for Markup widget
 * This file bundles the widget for use in any web environment (React, PHP, Vue, etc.)
 */

import React from "react";
import ReactDOM from "react-dom/client";
// Import standalone CSS (full Tailwind with preflight, compiled separately).
// rollup-plugin-string inlines this as a string at build time.
// @ts-ignore
import styles from "../dist/styles-standalone.css";
import { MarkupWidget } from "./components/MarkupWidget";
import type { MarkupConfig } from "./types";
import { useMarkupStore } from "./store";
import { isProductionSite } from "./utils/branch";

let root: ReturnType<typeof ReactDOM.createRoot> | null = null;

function init(
    config: Partial<MarkupConfig>,
    containerId: string = "markup-widget-root",
) {
    // Production site: don't add so much as a host element to the page.
    if (isProductionSite(config)) return;

    // Wait for DOM to be ready
    const initWidget = () => {
        // Create host element (the Shadow DOM host lives in the real DOM)
        let host = document.getElementById(containerId);
        if (!host) {
            host = document.createElement("div");
            host.id = containerId;
            host.setAttribute("data-markup-widget-root", "true");
            document.body.appendChild(host);
        }

        // Attach a Shadow DOM to isolate the widget's styles from the host page.
        // This prevents conflicts with the host site's Tailwind v4 cascade layers.
        let shadow = (host as any)._markupShadow as ShadowRoot | undefined;
        if (!shadow) {
            shadow = host.attachShadow({ mode: "open" });
            (host as any)._markupShadow = shadow;
        }

        // Inject styles into the shadow root (not document.head) so they are
        // fully isolated from the host page's CSS cascade.
        if (!shadow.getElementById("markup-widget-styles")) {
            const styleTag = document.createElement("style");
            styleTag.id = "markup-widget-styles";
            styleTag.textContent = styles as unknown as string;
            shadow.appendChild(styleTag);
        }

        // React mounts into a div inside the shadow root
        let mountPoint = shadow.getElementById("markup-widget-mount");
        if (!mountPoint) {
            mountPoint = document.createElement("div");
            mountPoint.id = "markup-widget-mount";
            shadow.appendChild(mountPoint);
        }

        // Create React root and mount widget
        root = ReactDOM.createRoot(mountPoint);
        root.render(
            <React.StrictMode>
                <MarkupWidget config={config} />
            </React.StrictMode>,
        );
    };

    // Check if DOM is ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", initWidget);
    } else {
        initWidget();
    }
}

// Both set the store directly and send the event. The event reaches a widget
// that's already running; the store covers the common `init(); open();` in one
// go, where the widget hasn't mounted (or started listening) yet — that open
// used to be lost.
function open() {
    const store = useMarkupStore.getState();
    store.setIsVisible(true);
    store.setIsOpen(true);
    window.dispatchEvent(new CustomEvent("markup-widget-open"));
}

function close() {
    useMarkupStore.getState().setIsOpen(false);
    window.dispatchEvent(new CustomEvent("markup-widget-close"));
}

function destroy() {
    if (root) {
        root.unmount();
        root = null;
    }
    const container = document.getElementById("markup-widget-root");
    if (container) {
        container.remove();
    }
}

// Create API object
const MarkupWidgetAPI = {
    init,
    open,
    close,
    destroy,
};

// Expose global API for script tag usage
if (typeof window !== "undefined") {
    (window as any).MarkupWidget = MarkupWidgetAPI;
}

export default MarkupWidgetAPI;
